/**
 * QuickAddSupplierModal
 *
 * Opens the same New Supplier form fields as SuppliersPage, but as a
 * floating Dialog attached to the Delivery Entry page. On successful
 * save it calls onCreated(newSupplier) so the parent can refresh its
 * supplier list and auto-select the new entry. Cancel / error leave
 * the parent delivery form completely untouched.
 *
 * Intentionally covers only the creation fields (no product-links section
 * — that is an edit-mode feature on the full Suppliers page). Uses the
 * same suppliersService.create() call and the same validation rules.
 */
import React, { useState } from 'react';
import { suppliersService } from '../services';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Loader2, Truck } from 'lucide-react';
import { getApiError } from '../lib/utils';
import { toast } from 'sonner';

const emptyForm = () => ({
  supplier_name: '',
  supplier_code: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  address: '',
  notes: '',
});

export default function QuickAddSupplierModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (v) => {
    if (!v) {
      // Reset form on close — delivery state is preserved in parent
      setForm(emptyForm());
    }
    onOpenChange(v);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.supplier_name?.trim()) {
      toast.error('Supplier name is required');
      return;
    }
    setSaving(true);
    try {
      const created = await suppliersService.create(form);
      toast.success(`Supplier "${created.supplier_name}" created`);
      setForm(emptyForm());
      onOpenChange(false);
      onCreated(created);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to create supplier'));
    } finally {
      setSaving(false);
    }
  };

  const f = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="quick-add-supplier-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            New Supplier
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label>Supplier Name *</Label>
              <Input
                value={form.supplier_name}
                onChange={f('supplier_name')}
                placeholder="e.g. Diageo"
                autoFocus
                data-testid="quick-supplier-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Code</Label>
              <Input
                value={form.supplier_code}
                onChange={f('supplier_code')}
                placeholder="e.g. DIA001"
                data-testid="quick-supplier-code-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Contact Name</Label>
              <Input value={form.contact_name} onChange={f('contact_name')}
                data-testid="quick-supplier-contact-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Contact Email</Label>
              <Input type="email" value={form.contact_email} onChange={f('contact_email')}
                data-testid="quick-supplier-contact-email" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Contact Phone</Label>
            <Input value={form.contact_phone} onChange={f('contact_phone')}
              data-testid="quick-supplier-contact-phone" />
          </div>

          <div className="space-y-1.5">
            <Label>Address</Label>
            <Textarea value={form.address} onChange={f('address')} rows={2}
              data-testid="quick-supplier-address" />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={f('notes')} rows={2}
              data-testid="quick-supplier-notes" />
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={saving}
              data-testid="quick-supplier-cancel-btn"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="quick-supplier-save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Supplier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
